import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../../services/api';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 250;

/**
 * Product-search bar for marketplace routes, opened by the navbar search
 * utility (`marketplace:open-search` CustomEvent) since the platform's
 * user-search bar is hidden here. Typing (2+ chars) shows live suggestions
 * from the BFF's Magento catalog search. Submit navigates to the PLP
 * (`/marketplace/search?q=…`, or browse-all when empty) and closes the
 * panel; clicking a suggestion navigates to that product's PDP (falling
 * back to a name search when the suggestion carries no urlKey).
 * Closes via the × button, Escape, or backdrop click.
 */
export default function MarketplaceSearchBar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [top, setTop] = useState(60);
  const [suggestions, setSuggestions] = useState([]);
  const [suggLoading, setSuggLoading] = useState(false);
  const inputRef = useRef(null);
  const suggSeq = useRef(0);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener('marketplace:open-search', onOpen);
    return () => window.removeEventListener('marketplace:open-search', onOpen);
  }, []);

  // Close on ANY route change (e.g. picking a suggestion navigates to a PDP).
  // More than one Navbar tree can mount this component, so per-instance
  // close-on-pick isn't enough — route change is the single reliable signal.
  const location = useLocation();
  useEffect(() => {
    setOpen(false);
  }, [location]);

  // Debounced live suggestions from the BFF (Magento catalog search).
  useEffect(() => {
    const term = query.trim();
    if (term.length < MIN_CHARS) {
      setSuggestions([]);
      setSuggLoading(false);
      return undefined;
    }
    setSuggLoading(true);
    const seq = ++suggSeq.current;
    const timer = setTimeout(() => {
      api
        .get('/api/v1/marketplace/search/suggestions', { params: { q: term } })
        .then((res) => {
          if (seq !== suggSeq.current) return;
          const list = res?.data?.data?.suggestions;
          setSuggestions(Array.isArray(list) ? list : []);
          setSuggLoading(false);
        })
        .catch(() => {
          if (seq !== suggSeq.current) return;
          setSuggestions([]);
          setSuggLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!open) return undefined;
    const navbar = document.querySelector('.navbar');
    setTop(navbar ? navbar.getBoundingClientRect().height : 60);
    const focusTimer = setTimeout(() => inputRef.current?.focus(), 60);
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(focusTimer);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const closePanel = () => {
    setOpen(false);
    setQuery('');
    setSuggestions([]);
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const term = query.trim();
    closePanel();
    // Submit navigates to the PLP: with the term when there is one,
    // browse-all when the field was empty.
    navigate(
      term
        ? `/marketplace/search?q=${encodeURIComponent(term)}`
        : '/marketplace/search'
    );
  };

  const onPick = (suggestion) => {
    closePanel();
    // Suggestion clicks navigate to the PDP; when a suggestion carries no
    // urlKey, fall back to a name search (honest, never a dead click).
    if (suggestion?.urlKey) {
      navigate(`/marketplace/product/${encodeURIComponent(suggestion.urlKey)}`);
    } else if (suggestion?.name) {
      navigate(`/marketplace/search?q=${encodeURIComponent(suggestion.name)}`);
    }
  };

  const money = (price) => {
    if (!price || !Number.isFinite(price.value)) return '';
    try {
      return price.currency
        ? new Intl.NumberFormat(i18n.language, {
            style: 'currency',
            currency: price.currency,
          }).format(price.value)
        : new Intl.NumberFormat(i18n.language).format(price.value);
    } catch {
      return String(price.value);
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="mp-search-backdrop" onClick={() => setOpen(false)} />
      <div
        className="mp-search-panel"
        role="dialog"
        aria-label={t('marketplace.utilities.search')}
        style={{ top }}
      >
        <div className="mp-search-box">
          <form className="mp-search-form" onSubmit={onSubmit}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('marketplace.header.searchPlaceholder')}
            aria-label={t('marketplace.header.searchPlaceholder')}
            className="mp-search-input"
          />
          <button
            type="button"
            className="mp-icon-btn mp-search-close mp-focusable"
            onClick={() => setOpen(false)}
            aria-label={t('marketplace.search.close')}
          >
            ×
          </button>
        </form>
        {query.trim().length >= MIN_CHARS && (
          <div className="mp-suggest" role="listbox" aria-label={t('marketplace.utilities.search')}>
            {suggLoading ? (
              <div className="mp-suggest-note">{t('marketplace.search.loading')}</div>
            ) : suggestions.length === 0 ? (
              <div className="mp-suggest-note">{t('marketplace.search.noMatches')}</div>
            ) : (
              suggestions.map((s) => (
                <button
                  key={s.sku}
                  type="button"
                  role="option"
                  aria-selected="false"
                  className="mp-suggest-item"
                  onClick={() => onPick(s)}
                >
                  <span className="mp-suggest-name">{s.name}</span>
                  <span className="mp-suggest-price">{money(s.price)}</span>
                </button>
              ))
            )}
          </div>
        )}
        </div>
      </div>
    </>
  );
}
